import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, hasPermission } from "../../context/AuthContext";
import { useForemanTeam } from "../../hooks/useForemanTeam";
import { PageHeader, SectionCard, Pill } from "../../components/Ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { INDIA_LOCALE, INDIA_TIME_ZONE } from "../../lib/indiaTime";
import { Users, ExternalLink, Flame } from "lucide-react";

function formatDuration(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function formatDt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(INDIA_LOCALE, {
    timeZone: INDIA_TIME_ZONE,
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ForemanTeamPage() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "production.foreman.users.manage");
  const {
    data,
    loading,
    submitting,
    label,
    operators,
    boiler,
    runtime,
    departmentPagePath,
    createOperator,
    updateOperator,
  } = useForemanTeam();

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", is_active: true });

  const resetForm = () => {
    setForm({ name: "", email: "", password: "", is_active: true });
    setEditTarget(null);
    setFormOpen(false);
  };

  const openCreate = () => {
    setEditTarget(null);
    setForm({ name: "", email: "", password: "", is_active: true });
    setFormOpen(true);
  };

  const openEdit = (op) => {
    setEditTarget(op);
    setForm({ name: op.name, email: op.email, password: "", is_active: op.is_active !== false });
    setFormOpen(true);
  };

  const saveOperator = async (e) => {
    e.preventDefault();
    if (editTarget) {
      const payload = { name: form.name, is_active: form.is_active };
      if (form.password) payload.password = form.password;
      const ok = await updateOperator(editTarget.id, payload);
      if (ok) resetForm();
      return;
    }
    const ok = await createOperator({ name: form.name, email: form.email, password: form.password });
    if (ok) resetForm();
  };

  if (loading && !data) {
    return <div className="py-16 text-center text-muted-foreground">Loading team…</div>;
  }

  const workspace = boiler?.workspace;
  const operatorShift = workspace?.operator_shift;
  const currentOperator = operatorShift?.current_operator_name || "—";

  return (
    <div className="space-y-6 sm:space-y-8 max-w-4xl mx-auto" data-testid="foreman-team-page">
      <PageHeader
        eyebrow="Production"
        title={label || "My team"}
        description="Monitor operators on shift and manage your department team. Open the department page to run operations when covering for an absent operator."
        actions={
          <Button asChild className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
            <Link to={departmentPagePath}>
              <span className="inline-flex items-center">
                <ExternalLink className="h-4 w-4 mr-2" />
                Open department
              </span>
            </Link>
          </Button>
        }
      />

      {boiler && (
        <SectionCard title="Boiler live status" description="Same view operators see — use Open department to take over or run the boiler.">
          <div className="space-y-3 text-sm">
            <p>
              Status:{" "}
              <Pill tone={workspace?.runtime?.status === "RUNNING" ? "success" : "default"}>
                {workspace?.runtime?.status || "STOPPED"}
              </Pill>
              {" · "}
              Current operator: <strong>{currentOperator}</strong>
            </p>
            {boiler.open_operator_sessions?.length > 0 && (
              <div className="rounded-xl border p-3 space-y-2">
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Open operator sessions</p>
                {boiler.open_operator_sessions.map((s) => (
                  <p key={s.id}>
                    <strong>{s.operator_name}</strong> — since {formatDt(s.started_at)}
                  </p>
                ))}
              </div>
            )}
            {boiler.recent_blowdowns?.length > 0 && (
              <div className="rounded-xl border p-3 space-y-2">
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-2">
                  <Flame className="h-4 w-4" /> Blowdown history
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b">
                        <th className="py-2 pr-3">Start</th>
                        <th className="py-2 pr-3">Stop</th>
                        <th className="py-2">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boiler.recent_blowdowns.map((row) => (
                        <tr key={row.id} className="border-b border-border/50">
                          <td className="py-2 pr-3">{formatDt(row.started_at)}</td>
                          <td className="py-2 pr-3">{row.is_active ? "In progress" : formatDt(row.ended_at)}</td>
                          <td className="py-2 font-mono tabular-nums">{formatDuration(row.duration_seconds)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {runtime && !boiler && (
        <SectionCard title="Department runtime" description={`${runtime.label} — section status summary.`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Stat label="Sections" value={runtime.summary?.total_sections ?? "—"} />
            <Stat label="Running" value={runtime.summary?.running_count ?? 0} />
            <Stat label="Stopped" value={runtime.summary?.stopped_count ?? 0} />
            <Stat label="Breakdown" value={runtime.summary?.breakdown_count ?? 0} />
          </div>
        </SectionCard>
      )}

      <SectionCard
        title="Operators"
        description="Operators reporting to you. Disabled accounts cannot sign in."
        actions={
          canManage ? (
            <Button type="button" onClick={openCreate} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
              <Users className="h-4 w-4 mr-2" />
              Add operator
            </Button>
          ) : null
        }
      >
        {operators.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No operators assigned to you yet.</p>
        ) : (
          <div className="space-y-3">
            {operators.map((op) => (
              <div key={op.id} className="rounded-xl border p-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-foreground">{op.name}</p>
                  <p className="text-sm text-muted-foreground">{op.email}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Pill tone={op.is_active !== false ? "success" : "warning"}>{op.is_active !== false ? "Active" : "Disabled"}</Pill>
                    {op.on_shift && <Pill tone="success">On shift</Pill>}
                    {op.is_current_operator && <Pill>Controlling now</Pill>}
                  </div>
                </div>
                {canManage && (
                  <Button type="button" variant="outline" size="sm" onClick={() => openEdit(op)}>
                    Manage
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {formOpen && canManage && (
        <SectionCard title={editTarget ? `Manage ${editTarget.name}` : "New operator"}>
          <form onSubmit={saveOperator} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required className="mt-1" />
            </div>
            {!editTarget && (
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required className="mt-1" />
              </div>
            )}
            <div className={editTarget ? "sm:col-span-2" : ""}>
              <Label>{editTarget ? "New password (optional)" : "Password"}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                required={!editTarget}
                minLength={editTarget && !form.password ? undefined : 6}
                className="mt-1"
              />
            </div>
            {editTarget && (
              <label className="sm:col-span-2 flex items-center gap-3">
                <Checkbox checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v === true }))} />
                <span>Account active (can sign in)</span>
              </label>
            )}
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
                {editTarget ? "Save changes" : "Create operator"}
              </Button>
            </div>
          </form>
        </SectionCard>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <p className="text-xs text-muted-foreground uppercase tracking-widest">{label}</p>
      <p className="text-xl font-semibold tabular-nums mt-1">{value}</p>
    </div>
  );
}
